// @flow
import type { Dispatch, Thunk } from 'types/redux';
import type { State as StoreState } from 'store/State';

// Actions
import { execScript } from 'store/reducers/scripts/actions';

// Selectors
import {
  getCobalt,
  getDotomiDealerId,
  getPixall,
  getShift
} from 'store/reducers/dealerData/selectors';
import { getVehicle } from 'store/reducers/appointment/selectors';
import { getMileageUnit } from 'store/reducers/dealerSettings/selectors';

// Utils
import { sendMessage } from 'utils/postMessage';
import { isObjEmpty } from 'utils/varValidation';

// Constants
import {
  CLICKTRACKING_TRIGGERS,
  COBALT_EVENTS,
  DOTOMI_EVENTS,
  PIXALL_EVENTS,
  SCRIPTS_EVENTS
} from 'constants/clickTracking';

export const clickTrackingEvent = (
  eventType: string,
  confirmationKey: ?string
): Thunk<void> => (dispatch: Dispatch, getState: () => StoreState) => {
  dispatch(scriptsClickTracking(eventType));
  dispatch(dotomiClickTrackingEvent(eventType));
  dispatch(cobaltClickTrackerEvent(eventType));
  dispatch(pixallClickTrackerEvent(eventType, confirmationKey));
  dispatch(hendricksClickTrackerEvent(eventType));
};

const scriptsClickTracking = (eventType: string): Thunk<void> => (
  dispatch: Dispatch,
  getState: () => StoreState
) => {
  let scriptName = '';
  switch (eventType) {
    case CLICKTRACKING_TRIGGERS.PREAMBLE:
      scriptName = SCRIPTS_EVENTS.PREAMBLE;
      break;
    case CLICKTRACKING_TRIGGERS.LOAD:
      scriptName = SCRIPTS_EVENTS.LOAD;
      break;
    case CLICKTRACKING_TRIGGERS.START:
      scriptName = SCRIPTS_EVENTS.START;
      break;
    case CLICKTRACKING_TRIGGERS.VEHICLE:
      scriptName = SCRIPTS_EVENTS.VEHICLE;
      break;
    case CLICKTRACKING_TRIGGERS.SERVICES:
      scriptName = SCRIPTS_EVENTS.SERVICES;
      break;
    case CLICKTRACKING_TRIGGERS.TIME:
      scriptName = SCRIPTS_EVENTS.TIME;
      break;
    case CLICKTRACKING_TRIGGERS.REVIEW:
      scriptName = SCRIPTS_EVENTS.REVIEW;
      break;
    case CLICKTRACKING_TRIGGERS.CONFIRM:
      scriptName = SCRIPTS_EVENTS.CONFIRM;
      break;
    case CLICKTRACKING_TRIGGERS.CANCEL:
      scriptName = SCRIPTS_EVENTS.CANCEL;
      break;
    default:
      return;
  }
  dispatch(execScript(scriptName));
};
const dotomiClickTrackingEvent = (eventType: string): Thunk<void> => (
  dispatch: Dispatch,
  getState: () => StoreState
) => {
  const dealerId = getDotomiDealerId(getState());
  if (dealerId) {
    const event = {
      clickTracker: 'dotomi',
      event: eventType,
      params: {}
    };
    switch (eventType) {
      case CLICKTRACKING_TRIGGERS.VEHICLE:
        event.params = DOTOMI_EVENTS.VEHICLE;
        break;
      case CLICKTRACKING_TRIGGERS.SERVICES:
        event.params = DOTOMI_EVENTS.SERVICES;
        break;
      case CLICKTRACKING_TRIGGERS.TIME:
        event.params = DOTOMI_EVENTS.TIME;
        break;
      case CLICKTRACKING_TRIGGERS.REVIEW:
        event.params = DOTOMI_EVENTS.REVIEW;
        break;
      case CLICKTRACKING_TRIGGERS.CONFIRM:
        event.params = DOTOMI_EVENTS.CONFIRM;
        break;
      default:
        return;
    }
    /* eslint-disable camelcase */
    event.params = { dealer_id: dealerId, ...event.params };
    /* eslint-enable camelcase */
    sendMessage(event);
  }
};

const cobaltClickTrackerEvent = (eventType: string): Thunk<void> => (
  dispatch: Dispatch,
  getState: () => StoreState
) => {
  const cobaltEnabled = getCobalt(getState());
  if (cobaltEnabled) {
    const event = {
      clickTracker: 'cobalt',
      event: eventType,
      params: {}
    };
    switch (eventType) {
      case CLICKTRACKING_TRIGGERS.LOAD:
        event.params = COBALT_EVENTS.LOAD;
        break;
      case CLICKTRACKING_TRIGGERS.START:
        event.params = COBALT_EVENTS.START;
        break;
      case CLICKTRACKING_TRIGGERS.VEHICLE:
        event.params = COBALT_EVENTS.VEHICLE;
        break;
      case CLICKTRACKING_TRIGGERS.SERVICES:
        event.params = COBALT_EVENTS.SERVICES;
        break;
      case CLICKTRACKING_TRIGGERS.TIME:
        event.params = COBALT_EVENTS.TIME;
        break;
      case CLICKTRACKING_TRIGGERS.REVIEW:
        event.params = COBALT_EVENTS.REVIEW;
        break;
      case CLICKTRACKING_TRIGGERS.CONFIRM:
        event.params = COBALT_EVENTS.CONFIRM;
        break;
      case CLICKTRACKING_TRIGGERS.CANCEL:
        event.params = COBALT_EVENTS.CANCEL;
        break;
      default:
        return;
    }
    sendMessage(event);
  }
};

const pixallClickTrackerEvent = (
  eventType: string,
  confirmationKey: ?string
): Thunk<void> => (dispatch: Dispatch, getState: () => StoreState) => {
  const pixallEnabled = getPixall(getState());
  const url = '*';
  // let url = null;
  // if (document.referrer) {
  //   url = new URL(document.referrer).origin;
  // }
  // if (!url) {
  //   return;
  // }
  let contentEvent = {};
  if (pixallEnabled) {
    switch (eventType) {
      case CLICKTRACKING_TRIGGERS.LOAD:
        contentEvent = PIXALL_EVENTS.LOAD;
        break;
      case CLICKTRACKING_TRIGGERS.START:
        contentEvent = PIXALL_EVENTS.START;
        break;
      case CLICKTRACKING_TRIGGERS.VEHICLE:
        contentEvent = PIXALL_EVENTS.VEHICLE;
        break;
      case CLICKTRACKING_TRIGGERS.SERVICES:
        contentEvent = PIXALL_EVENTS.SERVICES;
        break;
      case CLICKTRACKING_TRIGGERS.TIME:
        contentEvent = PIXALL_EVENTS.TIME;
        break;
      case CLICKTRACKING_TRIGGERS.REVIEW:
        contentEvent = PIXALL_EVENTS.REVIEW;
        break;
      case CLICKTRACKING_TRIGGERS.CONFIRM: {
        const vehicle = getVehicle(getState());
        const units = getMileageUnit(getState()) === 'km' ? 'km' : 'mi';
        const odometer = vehicle.mileage ? `${vehicle.mileage} ${units}` : null;
        contentEvent = PIXALL_EVENTS.CONFIRM;
        contentEvent.formEventId = confirmationKey;
        contentEvent.eventFields = {
          make: vehicle.make,
          year: vehicle.year,
          model: vehicle.model,
          trim: vehicle.trim,
          odometer,
          ...contentEvent.eventFields
        };
        break;
      }
      case CLICKTRACKING_TRIGGERS.CANCEL:
        contentEvent = PIXALL_EVENTS.CANCEL;
        break;
      default:
        return;
    }
  }
  if (window.opener) {
    window.opener.postMessage(JSON.stringify(contentEvent), url);
  } else if (window.top) {
    window.top.postMessage(JSON.stringify(contentEvent), url);
  }
};

export const triggerShiftEvent = (event: Object): Thunk<void> => (
  dispatch: Dispatch,
  getState: () => StoreState
) => {
  const shift = getShift(getState());
  if (!isObjEmpty(shift)) {
    window.dataLayer.push({
      event: 'ShiftEvent',
      ShiftConfig: event
    });
  }
};

export const hendricksClickTrackerEvent = (eventType: string): Thunk<void> => (
  dispatch: Dispatch,
  getState: () => StoreState
) => {
  if (eventType === CLICKTRACKING_TRIGGERS.CONFIRM) {
    const eventData = {
      trackEvent: {
        event: 'widgetUserEvent',
        action: 'clicked',
        element: 'Conversion button',
        elementCTA: 'Book it',
        result: 'Appointment Conversion',
        widgetId: 'xtime1',
        widgetName: 'xtime-scheduler',
        widgetState: 'Appointment creation'
      }
    };
    if (window.opener) {
      window.opener.postMessage(eventData, '*');
    } else if (window.top) {
      window.top.postMessage(eventData, '*');
    }
  }
};



// WEBPACK FOOTER //
// ./src/store/reducers/clickTracking/actions.js